import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { TaskCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Task Categories',
    description: 'Manage task categories',
    permissionModuleKey: 'task_category',
    route: {
      list: '/task_category',
      new: '/task_category/new',
      detail: (id: number) => `/task_category/${id}`,
      edit: (id: number) => `/task_category/${id}/edit`,
    },
    api: {
      list: '/p-crm-task-category',
      detail: (id: number) => `/p-crm-task-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search task categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['tc_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'tc_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: TaskCategory) => item.tc_descr,
        options: [],
      },
    ],
    applyFilter: (items: TaskCategory[], filters: { tc_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.tc_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.tc_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.tc_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Task Categorie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/task_category/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: TaskCategory) => {
      window.location.href = `/task_category/${item.id}`;
    },
    keyExtractor: (item: TaskCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new task categorie.',
    noSearchResults: 'No task categories found matching your search criteria.',
    noFilterResults: 'No task categories match the selected filters.',
  },
  delete: {
    title: 'Delete Task Categorie',
    message: 'Are you sure you want to delete this task categorie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete task categorie. Please try again.',
    handler: async (id: number) => {
      const { deleteTaskCategory } = await import('../../../services/mutations');
      await deleteTaskCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Task Categorie Details',
    notFoundTitle: 'Task Categorie not found',
    notFoundMessage: 'The task categorie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Task Categories',
    defaultTitle: 'Unnamed Task Categorie',
    getTitle: (item: TaskCategory) => {
      return item.tc_descr || `Task Categorie #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Task Categorie',
      subtitle: 'Add a new task categorie',
      submitText: 'Create Task Categorie',
    },
    edit: {
      title: 'Edit Task Categorie',
      subtitle: 'Update task categorie information',
      submitText: 'Update Task Categorie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading task categorie...',
    errorMessage: 'Failed to save task categorie. Please try again.',
    create: async (data: Partial<TaskCategory>) => {
      const { createTaskCategory } = await import('../../../services/mutations');
      return await createTaskCategory(data);
    },
    update: async (id: number, data: Partial<TaskCategory>) => {
      const { updateTaskCategory } = await import('../../../services/mutations');
      return await updateTaskCategory(id, data);
    },
  },
  fields: [
    {
      name: 'tc_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter task categorie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Task Categorie Information',
        icon: '📝',
        fields: ['tc_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<TaskCategory>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'tc_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: TaskCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.tc_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: TaskCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<TaskCategory> => {
  return {
    tc_descr: '',
  };
};

export const mapItemToFormData = (item: TaskCategory): Partial<TaskCategory> => {
  return {
    tc_descr: item.tc_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: TaskCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

